import type { MediaAsset, MediaPurpose } from '@recipes/contracts';
import { Button, Input, Label, cn } from '@recipes/ui';
import { ArrowDown, ArrowUp, Crop, ImagePlus, Loader2, Star, Trash2 } from 'lucide-react';
import { useId, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '@/lib/errors';
import { usePublicConfig } from '@/lib/hooks/use-public-config';
import { ImageEditorDialog } from './image-editor-dialog';
import {
  uploadImage,
  UploadValidationError,
  validateImageFile,
  type UploadPhase,
} from './use-upload';

export interface UploaderImage {
  assetId: string;
  url: string | null;
  thumbnailUrl: string | null;
  altText: string | null;
  /** PROCESSING assets show a spinner until the worker finishes. */
  status?: MediaAsset['status'];
}

export interface ImageUploaderProps {
  purpose: MediaPurpose;
  value: UploaderImage[];
  onChange: (value: UploaderImage[]) => void;
  multiple?: boolean;
  maxFiles?: number;
  /** When set, a "cover" radio is rendered per image. */
  coverAssetId?: string | null;
  onCoverChange?: (assetId: string | null) => void;
  /** Crop/rotate button per image (calls /media/:id/transform). */
  allowEdit?: boolean;
  /** Alt text field per image. */
  allowAltText?: boolean;
  label?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

interface PendingUpload {
  key: string;
  name: string;
  phase: UploadPhase;
  error?: string;
}

export function assetToUploaderImage(asset: MediaAsset): UploaderImage {
  return {
    assetId: asset.id,
    url: asset.url,
    thumbnailUrl: asset.thumbnailUrl ?? asset.url,
    altText: asset.altText,
    status: asset.status,
  };
}

/**
 * Shared image upload control (recipe cover/gallery, step images, avatar, cooking log photo).
 * Flow: upload intent → PUT to signed URL → complete → poll until processed.
 */
export function ImageUploader({
  purpose,
  value,
  onChange,
  multiple = false,
  maxFiles = multiple ? 30 : 1,
  coverAssetId,
  onCoverChange,
  allowEdit = true,
  allowAltText = true,
  label,
  disabled,
  className,
  'data-testid': testId,
}: ImageUploaderProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [editing, setEditing] = useState<UploaderImage | null>(null);
  const config = usePublicConfig();
  const maxBytes = config.data?.limits.maxUploadBytes ?? null;
  const valueRef = useRef(value);
  valueRef.current = value;

  const remainingSlots = Math.max(0, maxFiles - value.length);

  const handleFiles = async (files: FileList | File[]) => {
    const list = Array.from(files).slice(0, multiple ? remainingSlots : 1);
    if (list.length === 0) return;
    for (const file of list) {
      const key = crypto.randomUUID();
      try {
        validateImageFile(file, maxBytes);
      } catch (error) {
        const reason = error instanceof UploadValidationError ? error.reason : 'type';
        setPending((p) => [
          ...p,
          { key, name: file.name, phase: 'failed', error: t(`editor.images.error.${reason}`) },
        ]);
        continue;
      }
      setPending((p) => [...p, { key, name: file.name, phase: 'intent' }]);
      try {
        const asset = await uploadImage(file, {
          purpose,
          onPhase: (phase) =>
            setPending((p) => p.map((x) => (x.key === key ? { ...x, phase } : x))),
        });
        if (asset.status === 'FAILED') {
          setPending((p) =>
            p.map((x) =>
              x.key === key
                ? {
                    ...x,
                    phase: 'failed',
                    error: asset.errorMessage ?? t('editor.images.error.failed'),
                  }
                : x,
            ),
          );
          continue;
        }
        const next = multiple
          ? [...valueRef.current, assetToUploaderImage(asset)]
          : [assetToUploaderImage(asset)];
        onChange(next);
        if (onCoverChange && !coverAssetId && next.length === 1) onCoverChange(asset.id);
        setPending((p) => p.filter((x) => x.key !== key));
      } catch (error) {
        setPending((p) =>
          p.map((x) =>
            x.key === key ? { ...x, phase: 'failed', error: describeError(error) } : x,
          ),
        );
      }
    }
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) void handleFiles(event.target.files);
    event.target.value = '';
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (disabled) return;
    void handleFiles(event.dataTransfer.files);
  };

  const remove = (assetId: string) => {
    onChange(value.filter((v) => v.assetId !== assetId));
    if (coverAssetId === assetId)
      onCoverChange?.(value.find((v) => v.assetId !== assetId)?.assetId ?? null);
  };
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    onChange(next);
  };
  const setAlt = (assetId: string, altText: string) =>
    onChange(value.map((v) => (v.assetId === assetId ? { ...v, altText } : v)));

  const canAdd = !disabled && (multiple ? remainingSlots > 0 : true);

  return (
    <div className={cn('space-y-3', className)} data-testid={testId}>
      {canAdd && (
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center text-sm',
            dragOver && 'border-primary bg-accent',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <ImagePlus className="size-6 text-muted-foreground" aria-hidden />
          <Label htmlFor={inputId} className="cursor-pointer">
            {label ?? (multiple ? t('editor.images.addMany') : t('editor.images.addOne'))}
          </Label>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple={multiple}
            className="sr-only"
            onChange={onInputChange}
            disabled={disabled}
            data-testid="image-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            {t('editor.images.choose')}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t('editor.images.accepted')}
            {maxBytes
              ? ` · ${t('editor.images.maxSize', { mb: Math.round(maxBytes / 1024 / 1024) })}`
              : ''}
          </p>
        </div>
      )}

      {pending.length > 0 && (
        <ul className="space-y-1" aria-live="polite">
          {pending.map((p) => (
            <li key={p.key} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
              {p.phase !== 'failed' && <Loader2 className="size-4 animate-spin" aria-hidden />}
              <span className="min-w-0 flex-1 truncate">{p.name}</span>
              <span
                className={cn(
                  'text-xs',
                  p.phase === 'failed' ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {p.phase === 'failed' ? p.error : t(`editor.images.phase.${p.phase}`)}
              </span>
              {p.phase === 'failed' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPending((x) => x.filter((y) => y.key !== p.key))}
                >
                  {t('common.close')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {value.length > 0 && (
        <ul
          className={cn('grid gap-3', multiple ? 'sm:grid-cols-2' : 'grid-cols-1')}
          data-testid="image-list"
        >
          {value.map((image, index) => {
            const isCover = coverAssetId === image.assetId;
            return (
              <li
                key={image.assetId}
                className="flex gap-3 rounded-lg border p-2"
                data-testid="image-item"
              >
                <div className="relative size-24 shrink-0 overflow-hidden rounded-md bg-muted">
                  {image.thumbnailUrl || image.url ? (
                    <img
                      src={image.thumbnailUrl ?? image.url ?? ''}
                      alt={image.altText ?? ''}
                      className="size-full object-cover"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center">
                      <Loader2 className="size-5 animate-spin" aria-hidden />
                    </div>
                  )}
                  {isCover && (
                    <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                      {t('editor.images.cover')}
                    </span>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  {allowAltText && (
                    <div>
                      <Label htmlFor={`${inputId}-alt-${image.assetId}`} className="text-xs">
                        {t('editor.images.altText')}
                      </Label>
                      <Input
                        id={`${inputId}-alt-${image.assetId}`}
                        value={image.altText ?? ''}
                        onChange={(e) => setAlt(image.assetId, e.target.value)}
                        maxLength={300}
                        className="h-9"
                        disabled={disabled}
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {onCoverChange && (
                      <Button
                        type="button"
                        variant={isCover ? 'secondary' : 'ghost'}
                        size="sm"
                        aria-pressed={isCover}
                        onClick={() => onCoverChange(image.assetId)}
                        disabled={disabled}
                      >
                        <Star className={cn(isCover && 'fill-current')} aria-hidden />{' '}
                        {t('editor.images.setCover')}
                      </Button>
                    )}
                    {allowEdit && image.url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(image)}
                        disabled={disabled}
                      >
                        <Crop aria-hidden /> {t('editor.images.edit')}
                      </Button>
                    )}
                    {multiple && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('editor.images.moveUp')}
                          onClick={() => move(index, -1)}
                          disabled={disabled || index === 0}
                        >
                          <ArrowUp aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('editor.images.moveDown')}
                          onClick={() => move(index, 1)}
                          disabled={disabled || index === value.length - 1}
                        >
                          <ArrowDown aria-hidden />
                        </Button>
                      </>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('editor.images.remove')}
                      onClick={() => remove(image.assetId)}
                      disabled={disabled}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <ImageEditorDialog
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
          assetId={editing.assetId}
          src={editing.url ?? ''}
          altText={editing.altText}
          onSaved={(asset) =>
            onChange(
              valueRef.current.map((v) =>
                v.assetId === asset.id ? { ...v, ...assetToUploaderImage(asset) } : v,
              ),
            )
          }
        />
      )}
    </div>
  );
}
